import { useState, useEffect, useRef } from 'react'
import { Box, Text } from '@mantine/core'
import {
  computeChartYBounds,
  evaluateTideHeight,
  getNormalizedX,
  getNormalizedY,
  buildCurvePath,
} from '../_tideGraph'
import { useFormat } from '../../_units'

// Per-day tide curve, ported from the mobile app's Charts surface (dark theme,
// spec §2.5). Static layers (curve, dots, sun shading, axes) render during SSR;
// the live "now" marker and hover/scrub readout are added after mount.

const C = {
  card: '#0a405a',
  curve: '#b8ecf2',
  halo: 'rgba(6,37,52,0.7)',
  dot: '#ffffff',
  dayShade: 'rgba(255,200,50,0.05)',
  solar: 'rgba(255,215,0,0.22)',
  marker: 'rgba(30,40,55,0.9)',
  text: '#ffffff',
  subtext: '#5b8ba0',
  sun: '#FFD700',
}

// Fixed viewBox coordinate space; the SVG scales to the day-card width.
const VIEW_W = 640
const VIEW_H = 210
const X0 = 46 // left gutter (y-axis labels)
const X1 = 624 // right edge (16 pad)
const PW = X1 - X0 // plot width
const Y0 = 26 // plot top (= yMax)
const Y1 = 184 // plot bottom (= yMin)
const PH = Y1 - Y0 // plot height
const HOURS = [0, 3, 6, 9, 12, 15, 18, 21, 24] // 12 3 6 9 12 3 6 9 12 (12h labels)

export default function TideChart({ day, globalY, timezone }) {
  const fmt = useFormat()
  const segments = day?.segments || []
  const [mounted, setMounted] = useState(false)
  const [hover, setHover] = useState(null)
  const svgRef = useRef(null)

  useEffect(() => setMounted(true), [])

  if (!segments.length) {
    return (
      <Box
        style={{
          background: C.card,
          borderRadius: 10,
          height: 90,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text size="sm" style={{ color: C.subtext }}>
          No graph data available
        </Text>
      </Box>
    )
  }

  const scale = day.segments_scale || 1e6
  const dayStart = day.day_start
  const dayEnd = day.day_end
  const fbMin = day.y?.y_min ?? globalY?.y_min ?? 0
  const fbMax = day.y?.y_max ?? globalY?.y_max ?? 100
  const { yMin, yMax } = computeChartYBounds(segments, scale, fbMin, fbMax)

  const xPx = (t) => X0 + getNormalizedX(t, dayStart, dayEnd) * PW
  const yPx = (h) => Y0 + getNormalizedY(h, yMin, yMax) * PH

  const path = buildCurvePath(
    segments,
    scale,
    { x0: X0, y0: Y0, plotW: PW, plotH: PH, dayStart, dayEnd },
    yMin,
    yMax,
  )

  // Solar shading + markers (events within the day).
  const solar = (day.solar_events || [])
    .map((e) => {
      const t = Math.floor(new Date(e.timestamp).getTime() / 1000)
      return { t, x: xPx(t), rise: e.rise, time: fmt.time(e.timestamp, timezone) }
    })
    .filter((s) => s.t >= dayStart && s.t <= dayEnd)
  const sunrise = solar.find((s) => s.rise)
  const sunset = solar.find((s) => !s.rise)
  const dayLeft = sunrise ? sunrise.x : X0
  const dayRight = sunset ? sunset.x : X1

  // Hover/scrub: map a client x into the plot, then to a timestamp + height.
  const handleMove = (clientX) => {
    const svg = svgRef.current
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    if (!rect.width) return
    const vx = ((clientX - rect.left) / rect.width) * VIEW_W
    const cx = Math.max(X0, Math.min(X1, vx))
    const t = dayStart + ((cx - X0) / PW) * (dayEnd - dayStart)
    try {
      setHover({
        x: cx,
        h: evaluateTideHeight(segments, scale, t),
        time: fmt.timeUnix(t, timezone),
      })
    } catch {
      /* outside range */
    }
  }

  // Live "now" marker (client-only, today only, hidden while scrubbing).
  const nowSec = mounted ? Math.floor(Date.now() / 1000) : null
  let now = null
  if (mounted && !hover && nowSec >= dayStart && nowSec < dayEnd) {
    try {
      now = { x: xPx(nowSec), h: evaluateTideHeight(segments, scale, nowSec) }
    } catch {
      now = null
    }
  }

  const pill = (x, line1, line2) => {
    const w = 70
    const h = 34
    const cx = Math.max(X0 + w / 2, Math.min(X1 - w / 2, x))
    const cy = Y0 + PH / 2
    return (
      <g>
        <rect x={cx - w / 2} y={cy - h / 2} width={w} height={h} rx={4} fill={C.marker} />
        <text x={cx} y={cy - 3} textAnchor="middle" fill={C.text} fontSize={13} fontWeight={700}>
          {line1}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" fill={C.text} fontSize={11}>
          {line2}
        </text>
      </g>
    )
  }

  return (
    <Box style={{ borderRadius: 10, overflow: 'hidden', lineHeight: 0 }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        style={{ width: '100%', height: 'auto', display: 'block', touchAction: 'pan-y' }}
        onMouseMove={(e) => handleMove(e.clientX)}
        onMouseLeave={() => setHover(null)}
        onTouchMove={(e) => e.touches[0] && handleMove(e.touches[0].clientX)}
        onTouchEnd={() => setHover(null)}
      >
        {/* Card background */}
        <rect x={0} y={0} width={VIEW_W} height={VIEW_H} fill={C.card} />

        {/* Daytime shading + solar marker lines */}
        <rect
          x={dayLeft}
          y={Y0 - 4}
          width={Math.max(0, dayRight - dayLeft)}
          height={PH + 8}
          fill={C.dayShade}
        />
        {solar.map((s, i) => (
          <line
            key={`sun${i}`}
            x1={s.x}
            y1={Y0 - 4}
            x2={s.x}
            y2={Y1 + 4}
            stroke={C.solar}
            strokeWidth={1}
          />
        ))}

        {/* Sunrise / sunset labels (top row) */}
        {solar.map((s, i) => (
          <text
            key={`slbl${i}`}
            x={Math.max(60, Math.min(VIEW_W - 60, s.x))}
            y={15}
            textAnchor="middle"
            fill={C.sun}
            fontSize={12}
            fontWeight={600}
          >
            {`☀️ ${s.time} ${s.rise ? '↑' : '↓'}`}
          </text>
        ))}

        {/* Y-axis labels (yMax / mid / yMin) */}
        {[
          [yMax, Y0],
          [(yMin + yMax) / 2, Y0 + PH / 2],
          [yMin, Y1],
        ].map(([h, y], i) => (
          <text key={`y${i}`} x={X0 - 6} y={y + 3} textAnchor="end" fill={C.text} fontSize={11}>
            {fmt.heightAxis(h)}
          </text>
        ))}

        {/* X-axis hour ticks */}
        {HOURS.map((hr) => {
          const t = dayStart + hr * 3600
          if (t > dayEnd) return null
          return (
            <text
              key={`x${hr}`}
              x={xPx(t)}
              y={Y1 + 16}
              textAnchor="middle"
              fill={C.subtext}
              fontSize={11}
            >
              {hr % 12 || 12}
            </text>
          )
        })}

        {/* Tide curve: halo under, bright line over */}
        <path
          d={path}
          fill="none"
          stroke={C.halo}
          strokeWidth={4.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={path}
          fill="none"
          stroke={C.curve}
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Extrema dots + time labels (high below, low above) */}
        {(day.extrema || []).map((e, i) => {
          const x = xPx(e.t)
          const y = yPx(e.h)
          const high = e.kind === 'H'
          return (
            <g key={`e${i}`}>
              <circle cx={x} cy={y} r={7} fill={C.halo} />
              <circle cx={x} cy={y} r={5} fill={C.dot} />
              <text
                x={x}
                y={high ? y + 18 : y - 11}
                textAnchor="middle"
                fill={C.text}
                fontSize={11}
              >
                {fmt.timeShortUnix(e.t, timezone)}
              </text>
            </g>
          )
        })}

        {/* Live now-marker */}
        {now && (
          <>
            <line x1={now.x} y1={Y0 - 4} x2={now.x} y2={Y1 + 4} stroke={C.marker} strokeWidth={1} />
            {pill(now.x, fmt.height(now.h), 'Now')}
          </>
        )}

        {/* Hover/scrub readout */}
        {hover && (
          <>
            <line
              x1={hover.x}
              y1={Y0 - 4}
              x2={hover.x}
              y2={Y1 + 4}
              stroke={C.marker}
              strokeWidth={1}
            />
            {pill(hover.x, fmt.height(hover.h), hover.time)}
          </>
        )}
      </svg>
    </Box>
  )
}
